// The one place the site talks to /api/apply (CMSY-108).
//
// Both forms previously called `fetch('/api/apply', …)` directly, in four
// places. The server now wants a signed token with each submission
// (`api/_form-token.js`), and four call sites each doing their own fetch,
// caching and retry is four chances to get the retry wrong — so the fetch
// moved here and the call sites pass a body.
//
// ── The applicant must never lose a submission to this ─────────────────────
// That is the whole design constraint, and it is not theoretical: CMSY-33 is
// a real person whose application was quarantined twice by a guard that was
// sure she was a robot. So:
//   · a token that cannot be fetched is simply not sent — the server treats a
//     missing token exactly like an unarmed server and stores the row;
//   · a token the server rejects is discarded, refetched ONCE, and the submit
//     retried, which covers the ordinary case of a tab left open past the TTL;
//   · every failure path here ends in a normal fetch, never in a thrown error
//     that would strand a filled-in form.
// The retry is once, not a loop: a server rejecting a token it just minted is
// broken in a way more requests cannot fix.

let cached: string | null = null;
let inFlight: Promise<string | null> | null = null;

// 204 means the server is not armed (FORM_TOKEN_SECRET unset) — the normal
// state until someone sets it in Vercel. Null from here is not an error and
// must not be logged as one.
async function fetchToken(): Promise<string | null> {
  try {
    const r = await fetch('/api/form-token', { method: 'GET' });
    if (r.status === 204 || !r.ok) return null;
    const data = (await r.json()) as { token?: string };
    return typeof data?.token === 'string' ? data.token : null;
  } catch {
    return null; // offline, blocked, adblocked — submit without one
  }
}

// Shared promise so a page that fires a partial and a submit at once mints one
// token, not two. Cleared on settle so a later refresh can happen.
function token(): Promise<string | null> {
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = fetchToken().then((t) => {
      cached = t;
      inFlight = null;
      return t;
    });
  }
  return inFlight;
}

/** Warm the token while the applicant is still reading the first question. */
export function primeFormToken(): void {
  void token();
}

/** Drop the cached token; the next call mints a fresh one. */
export function resetFormToken(): void {
  cached = null;
}

const post = (body: unknown, formToken: string | null) =>
  fetch('/api/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formToken ? { ...(body as object), formToken } : body),
  });

/**
 * POST to /api/apply with a form token, refetching and retrying once if the
 * server says the token is stale. Resolves with the Response either way — the
 * callers already interpret status codes (429 is a success, and so on), and
 * changing that here would be a second place where meaning lives.
 */
export async function postApplication(body: unknown): Promise<Response> {
  const first = await post(body, await token());
  if (first.status !== 403) return first;

  // Only a token complaint earns a retry. A 403 for any other reason is the
  // server's answer, and resending would just repeat it.
  const data = await first
    .clone()
    .json()
    .catch(() => ({} as { code?: string }));
  if (data?.code !== 'form_token') return first;

  resetFormToken();
  return post(body, await token());
}
